<template>
  <div>
    <a-table
      :columns="columns"
      :loading="loadingSummary"
      :data-source="subprojectsSummary"
      :pagination="false"
      :scroll="{ x: true }"
      :row-key="(subproject) => subproject.name"
    >
      <template #actions="text, subprojectSummaryRow">
        <span v-if="canManageTransfers && isTransferableRow(subprojectSummaryRow)">
          <a-icon
            type="swap"
            class="action-default"
            @click="openTransferModal(subprojectSummaryRow)"
          />
        </span>
      </template>

      <template #footer>
        <strong>Total:</strong>
        {{ subprojectTotalHours }}h
      </template>
    </a-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, getCurrentInstance } from "vue";
import { BaseAclInterface } from "@/modules/authentication/types";
import {
  ReportInterface,
  ReportsSummaryFilterInterface,
  SubprojectReportInterface,
} from "@/modules/reports/types";
import {
  NewSubprojectInterface,
  ProjectInterface,
  TransferSourceContext,
} from "@/modules/projects/types";
import { apiGetReports } from "@/modules/reports/_utils/api";

interface SubprojectSummaryRow {
  name?: string;
  total?: number;
}

interface SubprojectContext {
  id: number;
  name: string;
  destination_project_id?: number | null;
}

// Props
const props = defineProps({
  project: { type: Object as () => ProjectInterface, default: undefined },
  subprojectsSummary: { type: Array as () => Array<any>, default: () => [] },
  subprojects: { type: Array as () => Array<NewSubprojectInterface>, default: () => [] },
  reportsPage: { type: Array as () => Array<ReportInterface>, default: () => [] },
  filters: { type: Object as () => ReportsSummaryFilterInterface, default: undefined },
  acl: { type: Object as () => BaseAclInterface, default: undefined },
  loadingSummary: { type: Boolean, default: false },
});

const emits = defineEmits(["open-transfer-modal"]);

// Instances
const instance = getCurrentInstance();
const $message = instance?.proxy?.$message;

// Data properties
const columns = ref<Array<any>>([
  {
    title: "Subproject",
    dataIndex: "name",
    key: "name",
  },
  {
    title: "Total hours",
    dataIndex: "total",
    key: "total",
  },
  {
    title: "Invoiced hours",
    dataIndex: "invoiced_hours",
    key: "invoiced_hours",
  },
  {
    title: "Total cost",
    dataIndex: "total_cost_cents",
    key: "total_cost",
    customRender: (text: number, record: { total_cost_currency: string }) => {
      if (!text) {
        return "";
      }
      return (text / 100).toString() + " " + record.total_cost_currency;
    },
  },
  {
    title: "Actions",
    key: "actions",
    scopedSlots: { customRender: "actions" },
  },
]);

// Computed properties
const canManageTransfers = computed((): boolean => {
  return Boolean(
    props.acl &&
    (props.acl.isAdmin || props.acl.isCr || props.acl.isProjectManager || props.acl.isFinancial)
  );
});

const subprojectTotalHours = computed((): number => {
  return (props.subprojectsSummary || []).reduce(
    (total: number, subproject: SubprojectSummaryRow) => {
      return total + (Number(subproject?.total) || 0);
    },
    0
  );
});

const findSubprojectByName = (name?: string): SubprojectContext | undefined => {
  if (!name || name === "Uncategorized") {
    return undefined;
  }
  const fromSubprojectsList = props.subprojects.find(
    (subproject: NewSubprojectInterface) => subproject.name === name && !!subproject.id
  );
  if (fromSubprojectsList?.id) {
    return {
      id: Number(fromSubprojectsList.id),
      name: String(fromSubprojectsList.name),
      destination_project_id: fromSubprojectsList.destination_project_id,
    };
  }

  for (const report of props.reportsPage) {
    const matchingSubprojectReport = (report.subproject_reports || []).find(
      (subprojectReport: SubprojectReportInterface) =>
        subprojectReport.name === name && !!subprojectReport.subproject_id
    );
    if (matchingSubprojectReport?.subproject_id) {
      return { id: Number(matchingSubprojectReport.subproject_id), name };
    }
  }

  return undefined;
};

const findReportsForSubproject = (subproject: SubprojectContext): Array<ReportInterface> => {
  return props.reportsPage.filter((report: ReportInterface) =>
    reportHasSubproject(report, subproject.id)
  );
};

const reportHasSubproject = (report: ReportInterface, subprojectId: number): boolean => {
  return (report.subproject_reports || []).some(
    (subprojectReport: SubprojectReportInterface) =>
      Number(subprojectReport.subproject_id) === subprojectId
  );
};

const isTransferableRow = (row: SubprojectSummaryRow): boolean => {
  const subproject = findSubprojectByName(row?.name);
  if (!subproject) {
    return false;
  }
  return Number(row?.total) > 0 || findReportsForSubproject(subproject).length > 0;
};

// TODO: Once the reports API supports filtering by subproject_id, this function should
// accept a subproject parameter and fetch only reports for that specific subproject,
// rather than fetching all reports for the project and filtering client-side.
const getAllReportsForCurrentSummary = async (): Promise<Array<ReportInterface>> => {
  if (!props.project?.id) {
    return props.reportsPage;
  }

  const pageSize = 100;
  const fetchPage = (page: number) =>
    apiGetReports({
      ...props.filters,
      project_id: props.project?.id,
      page,
      page_size: pageSize,
    });

  const firstPageResponse = await fetchPage(1);
  const firstPageReports = firstPageResponse.data.data;
  const lastPage = Number(firstPageResponse.data.meta?.last_page || 1);
  const remainingPageNumbers = Array.from({ length: Math.max(lastPage - 1, 0) }, (_, index) => {
    return index + 2;
  });
  const remainingPageResponses = await Promise.all(remainingPageNumbers.map(fetchPage));
  return remainingPageResponses.reduce<Array<ReportInterface>>(
    (reports, response) => [...reports, ...response.data.data],
    firstPageReports
  );
};

const buildTransferContext = async (
  subproject: SubprojectContext
): Promise<TransferSourceContext | undefined> => {
  const reports = await getAllReportsForCurrentSummary();
  const sourceReports = reports.filter((report) => reportHasSubproject(report, subproject.id));
  const report = sourceReports[0];
  if (!report) {
    return undefined;
  }
  const sourceSubprojectReport = (report.subproject_reports || []).find(
    (subprojectReport: SubprojectReportInterface) =>
      Number(subprojectReport.subproject_id) === Number(subproject.id)
  );

  return {
    sourceType: "subproject_all",
    subproject: {
      id: subproject.id,
      name: subproject.name,
      destination_project_id: subproject.destination_project_id,
      report_id: report.id,
      transfer_amount: sourceSubprojectReport?.amount,
    },
    report,
    sourceReports,
  };
};

const openTransferModal = async (row: SubprojectSummaryRow): Promise<void> => {
  const subproject = findSubprojectByName(row?.name);
  if (!subproject) {
    return;
  }

  try {
    const transferContext = await buildTransferContext(subproject);
    if (!transferContext) {
      $message?.error("Couldn't find reports for this subproject transfer.", 3);
      return;
    }

    emits("open-transfer-modal", transferContext);
  } catch {
    $message?.error("Couldn't initialize transfer form.", 3);
    return;
  }
};
</script>
